import { useCallback, useEffect, useRef, useState } from 'react'
import { Stripe, StripeElements } from '@stripe/stripe-js'

import Button from '../button/Button'
import { PAYMENT_ELEMENT_APPEARANCE } from '../../_defaults/payment-element-appearance'
import { getStripeClient } from '../../_utilities/stripe-loader'
import { createCreditCheckPayment } from '../../_data/verifications'
import { CREDIT_CHECK_PRICE_CENTS, formatPrice } from './helpers'
import { CreditCheckPaymentStepProps } from './types'
import {
  CostNote,
  ErrorBanner,
  Footer,
  PaymentElementSlot,
  PaymentLoadingNote,
  PaymentStack,
  PriceBox,
  PriceTotalRow,
  SectionTitle,
} from './styled'

type PaymentStepState = 'loading' | 'ready' | 'confirming' | 'unavailable'

/**
 * Takes the credit check fee, then hands the authorisation back to the modal so
 * it can place the order.
 *
 * The card is only ever authorised here, never charged. The server captures the
 * money once Certn has accepted the case and releases it if anything fails,
 * which is why this step reports an authorisation rather than a payment and why
 * a failure downstream can honestly tell the renter they were not charged.
 *
 * @param {CreditCheckPaymentStepProps} props
 * @returns {JSX.Element}
 */
const CreditCheckPaymentStep = ({
  quickPassUserId,
  isPlacingOrder,
  onBack,
  onAuthorized,
}: CreditCheckPaymentStepProps): JSX.Element => {
  const [stepState, setStepState] = useState<PaymentStepState>('loading')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const elementContainerRef = useRef<HTMLDivElement | null>(null)
  const stripeRef = useRef<Stripe | null>(null)
  const elementsRef = useRef<StripeElements | null>(null)
  const paymentIntentIdRef = useRef<string>('')

  useEffect(() => {
    let isActive = true

    const setUpPayment = async (): Promise<void> => {
      const [payment, stripe] = await Promise.all([
        createCreditCheckPayment(quickPassUserId),
        getStripeClient(),
      ])

      if (isActive === false) {
        return
      }

      if (
        payment === null ||
        stripe === null ||
        elementContainerRef.current === null
      ) {
        setStepState('unavailable')
        setErrorMessage(
          'We could not start the payment just now. Please try again in a moment.',
        )
        return
      }

      stripeRef.current = stripe
      paymentIntentIdRef.current = payment.paymentIntentId

      const elements = stripe.elements({
        clientSecret: payment.clientSecret,
        appearance: PAYMENT_ELEMENT_APPEARANCE,
      })
      elementsRef.current = elements

      const paymentElement = elements.create('payment')
      paymentElement.mount(elementContainerRef.current)
      setStepState('ready')
    }

    void setUpPayment()

    return () => {
      isActive = false
      const elements = elementsRef.current
      if (elements !== null) {
        const paymentElement = elements.getElement('payment')
        if (paymentElement !== null) {
          paymentElement.destroy()
        }
      }
      elementsRef.current = null
      stripeRef.current = null
    }
  }, [quickPassUserId])

  const handleConfirm = useCallback(async (): Promise<void> => {
    const stripe = stripeRef.current
    const elements = elementsRef.current

    if (stripe === null || elements === null) {
      return
    }

    setErrorMessage('')
    setStepState('confirming')

    // Deliberately no `return_url`. This is a modal holding unsaved form state,
    // and a full page redirect would drop the renter back on the verifications
    // page with a live authorisation and no order behind it, which then quietly
    // expires days later. The order is card only and card 3D Secure runs in an
    // overlay rather than a redirect, so the rare method that genuinely needs to
    // navigate away fails here with a message instead, and nothing is taken.
    const result = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    })

    if (result.error !== undefined) {
      setErrorMessage(
        result.error.message ??
          'Your card could not be authorised. Please try another card.',
      )
      setStepState('ready')
      return
    }

    onAuthorized(paymentIntentIdRef.current)
  }, [onAuthorized])

  const isBusy = stepState === 'confirming' || isPlacingOrder

  return (
    <PaymentStack>
      <SectionTitle>Payment</SectionTitle>

      <PriceBox>
        <PriceTotalRow>
          <span>Credit check</span>
          <span>{formatPrice(CREDIT_CHECK_PRICE_CENTS)}</span>
        </PriceTotalRow>
      </PriceBox>

      <CostNote>
        We place a hold on your card now and only take the money once the check
        has actually been started. If anything goes wrong, the hold is released
        and you are not charged.
      </CostNote>

      {stepState === 'loading' ? (
        <PaymentLoadingNote>Getting the payment form ready.</PaymentLoadingNote>
      ) : null}

      <PaymentElementSlot ref={elementContainerRef} />

      {errorMessage !== '' ? <ErrorBanner>{errorMessage}</ErrorBanner> : null}

      <Footer>
        <Button
          type="button"
          variant="secondary"
          size="small"
          disabled={isBusy}
          onClick={onBack}
        >
          Back
        </Button>
        <Button
          type="button"
          variant="primary"
          size="small"
          isLoading={isBusy}
          disabled={stepState !== 'ready'}
          onClick={() => {
            void handleConfirm()
          }}
        >
          {`Pay ${formatPrice(CREDIT_CHECK_PRICE_CENTS)} and run my check`}
        </Button>
      </Footer>
    </PaymentStack>
  )
}

export default CreditCheckPaymentStep
